import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Autocomplete,
  Typography,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import LoadingState from "../common/LoadingState";
import UserAvatar from "../common/UserAvatar";
import { TestId } from "../../constants/testIds";
import { type User, UserRole } from "../../types/user";
import type { OperationsTeam } from "../../types/operationsTeam";

type TranslationKeys = keyof typeof translations.en;

interface OperationsTeamFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; userIds: string[] }) => Promise<void>;
  error: string | null;
  users: User[];
  initialTeam?: OperationsTeam | null;
}

const OperationsTeamForm: React.FC<OperationsTeamFormProps> = ({
  open,
  onClose,
  onSubmit,
  error,
  users,
  initialTeam = null,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const operationsUsers = useMemo(
    () => users.filter((user) => user.role === UserRole.STAFF_OPERATIONS && !user.deactivated),
    [users],
  );

  useEffect(() => {
    if (!open) return;

    setName(initialTeam?.name || "");
    setSelectedUsers(
      operationsUsers.filter((user) => initialTeam?.userIds.includes(user._id) ?? false),
    );
    setValidationErrors([]);
  }, [open, initialTeam, operationsUsers]);

  const validateForm = (): boolean => {
    const errors: string[] = [];
    if (!name.trim()) {
      errors.push("Team name is required");
    }
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSubmit({
        name: name.trim(),
        userIds: selectedUsers.map((user) => user._id),
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <LoadingState message={initialTeam ? "Updating team..." : "Creating team..."} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid={TestId.OPS_TEAM.FORM}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {initialTeam ? t("Edit Team" as TranslationKeys) : t("Add Team" as TranslationKeys)}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" component="div">
                {validationErrors.map((validationError) => (
                  <div key={validationError}>{t(validationError as TranslationKeys)}</div>
                ))}
              </Typography>
            </Alert>
          )}

          <TextField
            autoFocus
            margin="dense"
            label={t("Team Name" as TranslationKeys)}
            name="name"
            type="text"
            fullWidth
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            sx={{ mb: 2 }}
            inputProps={{ "data-testid": TestId.OPS_TEAM.NAME }}
          />

          <Autocomplete
            multiple
            options={operationsUsers}
            value={selectedUsers}
            onChange={(_, value) => setSelectedUsers(value)}
            getOptionLabel={(user) => user.name}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            renderOption={(props, user) => (
              <Box component="li" {...props} key={user._id} sx={{ display: "flex", gap: 1 }}>
                <UserAvatar user={user} size={24} />
                <Typography variant="body2">{user.name}</Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Team Members" as TranslationKeys)}
                inputProps={{
                  ...params.inputProps,
                  "data-testid": TestId.OPS_TEAM.USER_IDS,
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("Cancel" as TranslationKeys)}</Button>
          <Button type="submit" variant="contained" disabled={!name.trim()}>
            {initialTeam ? t("save changes" as TranslationKeys) : t("Add Team" as TranslationKeys)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default OperationsTeamForm;
